import type { HillState } from "./sim/state";
import { WOBBLE_SPEED } from "./sim/tuning";

// Bomb the Hill's points (#205): three scores, each shown on its own, then
// weighted and summed into the run's total. Distance is metres down the
// hill (close calls included). Speed pays for each metre ridden fast, more
// the faster, with the wobble zone paying again once you're back under it
// still on the board. Tricks are the combos you land, but only worth their
// points once you're well down the hill and moving, so you have to go down
// the hill to score: combos at the top while barely rolling pay nothing.

/** Each score's weight in the total: a metre down is worth ten points, speed points one each, and trick points half. */
export const HILL_WEIGHTS = { distance: 10, speed: 1, tricks: 0.5 } as const;

/** Speed points start at this speed (m/s)... */
export const SPEED_FROM = 12;
/** ...and pay this many a metre down at wobble speed, in proportion between (and past it). */
export const SPEED_POINTS = 6;
/** A metre in the wobble zone pays its speed points this many times again, once you're back under wobble speed still riding. */
export const WOBBLE_PAY = 1;
/** Tricks pay in full only this far down (m), in proportion before it... */
export const TRICK_RAMP = 120;
/** ...and only landed this fast (m/s), nothing at or under TRICK_SLOW (the hill's push speed is 5). */
export const TRICK_SLOW = 7;
export const TRICK_FAST = 16;
/** A combo kept open longer than this (s) under TRICK_SLOW pays nothing when it lands: no camping. */
export const CAMP_TIME = 1.5;

/** A run's scores (#205): each on its own, and the weighted total. */
export interface HillScore {
  /** Metres down, close calls included. */
  distance: number;
  speed: number;
  tricks: number;
  total: number;
}

/** The run's scores now: the speed in the wobble zone not yet banked doesn't count. */
export function hillScore(h: Pick<HillState, "distance" | "bonus" | "speedPts" | "trickPts">): HillScore {
  const distance = Math.floor(h.distance + h.bonus);
  const speed = Math.floor(h.speedPts);
  const tricks = Math.floor(h.trickPts);
  return { distance, speed, tricks, total: weighted("distance", distance) + weighted("speed", speed) + weighted("tricks", tricks) };
}

/** One score's share of the total: the score times its weight, whole, so the end card's rows add up to the total. */
export function weighted(part: keyof typeof HILL_WEIGHTS, score: number): number {
  return Math.round(score * HILL_WEIGHTS[part]);
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * One step of the run's scoring: `dz` metres further down at `speed` (m/s),
 * with a combo open or not. Pays the speed points, keeps the wobble zone's
 * pot until you're back under wobble speed, and times how long an open
 * combo has spent barely moving.
 */
export function scoreStep(h: HillState, speed: number, dz: number, dt: number, comboOpen: boolean): void {
  if (dz > 0) {
    const pay = SPEED_POINTS * Math.max(0, (speed - SPEED_FROM) / (WOBBLE_SPEED - SPEED_FROM)) * dz;
    h.speedPts += pay;
    if (speed > WOBBLE_SPEED) h.wobblePts += pay * WOBBLE_PAY;
  }
  if (speed <= WOBBLE_SPEED && h.wobblePts > 0) {
    h.speedPts += h.wobblePts;
    h.wobblePts = 0;
  }
  h.slowT = comboOpen ? h.slowT + (speed < TRICK_SLOW ? dt : 0) : 0;
}

/** How much of a combo's points count on the hill: how far down (`distance`, m) and how fast (`speed`, m/s, squared) it landed, nothing if it camped. */
export function trickWeight(distance: number, speed: number, slowT: number): number {
  if (slowT > CAMP_TIME) return 0;
  // Squared on speed, so a slow roll's combos pay next to nothing, not a share in proportion.
  return clamp01(distance / TRICK_RAMP) * clamp01((speed - TRICK_SLOW) / (TRICK_FAST - TRICK_SLOW)) ** 2;
}

/** A combo of `points` landed at `speed` (m/s): what it adds to the run's trick score. */
export function scoreTricks(h: HillState, points: number, speed: number): number {
  const add = Math.round(points * trickWeight(h.distance, speed, h.slowT));
  h.trickPts += add;
  h.slowT = 0;
  return add;
}

/** The run's over: the wobble zone's pot not yet banked is lost (you fell in it, or it ended before you got back under). */
export function endScore(h: HillState): void {
  h.wobblePts = 0;
}
